/* eslint-disable react/no-multi-comp */
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import PropTypes from 'prop-types';
import { MdInfo } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { RunReportDropdown } from '@modules/advancement/common';
import { itemsPerSubUnitSel } from '@modules/advancement/packRoster';
import { PendingItemsTabs } from '@modules/advancement/pendingItems/constants';
import { pendingItemsActiveTabSel } from '@modules/advancement/pendingItems/duck';
import { isUserInSubUnitChecker } from '@modules/advancement/pendingItems/utils';
import { GroupBy } from '@modules/advancement/unitAdvancement/components/GroupBy/GroupBy';
import { groupBySubUnitToApprove } from '@modules/advancement/unitAdvancement/duck/utilsTyped';
import { SBL_4463_betaUnitsSel } from '@modules/shared/duck/selectors/flags.selectors';
import {
  ApproveAdvancementsButton,
  DeclineAdvancementsButton,
} from '@progress/components';
import {
  Program,
  Tooltip,
  advancementStatuses,
  navigateToPurchaseOrderDetails,
  selectedOrganizationSel,
} from '@shared';
import { FilterButton, PaginationTotal, Table } from '@shared/components';
import { hasPermissionSel, permissions } from '@user';
import { toggleArrayItem } from '@utils';

import { setFromActivityLogs } from '../../../../../progress/approveAdvancements';
import PendingItemsFilterCard from '../PendingItemsFilterCard';
import PendingItemsCustomRow from './PendingItemsCustomRow';
import styles from './PendingItemsTable.less';
import columns from './pendingItemsTableColumns';
import advancementsTabColumns from './pendingItemsTableColumnsTyped';

const PendingItemsTable = ({
  items,
  loading,
  sorter,
  showFilter = true,
  selectedRowKeys,
  isOffline,
  onSetSelectedRowKeys,
  onGoToYouthProfile,
  onGoToAdultProfile,
  onSorterChange,
  showPOID,
  showUpdate = true,
  isAwarding = false,
}) => {
  const dispatch = useDispatch();
  const canApprove = useSelector(state =>
    hasPermissionSel(state, permissions.APPROVE_ADVANCEMENTS),
  );
  const activeTab = useSelector(pendingItemsActiveTabSel);
  const showForBetaUnits = useSelector(SBL_4463_betaUnitsSel);
  const currentOrg = useSelector(selectedOrganizationSel);
  const itemsPerSubUnit = useSelector(itemsPerSubUnitSel);

  const showDeclineButton = activeTab === PendingItemsTabs.ACTIVITIES;
  const areAnyAdvancementsSelected = selectedRowKeys.length > 0;
  const selectedAdvancements = useMemo(
    () => items.filter(({ key }) => selectedRowKeys.includes(key)),
    [items, selectedRowKeys],
  );
  const handleSelectAll = useCallback(
    isSelectAll => {
      const selectedItemsKeys = isSelectAll ? items.map(({ key }) => key) : [];
      onSetSelectedRowKeys(selectedItemsKeys);
    },
    [items, onSetSelectedRowKeys],
  );

  const disableDecline = !areAnyAdvancementsSelected;

  const handleSelectRow = useCallback(
    record => {
      if (!isUserInSubUnitChecker(record) || record.isHeader || !showUpdate)
        return;

      const newSelectedKeys = toggleArrayItem(selectedRowKeys, record.key);
      onSetSelectedRowKeys(newSelectedKeys);
    },
    [showUpdate, selectedRowKeys, onSetSelectedRowKeys],
  );

  const handleTableChange = useCallback(
    (_pagination, _filters, sorter) => {
      onSorterChange(sorter);
    },
    [onSorterChange],
  );

  const handleGoToPODetails = useCallback(
    poid => {
      dispatch(navigateToPurchaseOrderDetails(poid));
    },
    [dispatch],
  );

  const onGoToPODetails = showPOID ? handleGoToPODetails : false;

  const renderPaginationTotal = useCallback(
    total => (
      <div className={styles.pendinTablePaginationContainer}>
        <Tooltip
          title={
            <FormattedMessage id="advancement.PendingItems.totalDisclaimer" />
          }
        >
          <MdInfo className={styles.infoIcon} />
        </Tooltip>
        <PaginationTotal total={total} selected={selectedRowKeys.length} />
      </div>
    ),
    [selectedRowKeys],
  );

  const pagination = {
    showTotal: renderPaginationTotal,
  };

  const rowSelection = {
    selectedRowKeys,
    columnTitle: <></>,
    onChange: onSetSelectedRowKeys,
    onSelectAll: handleSelectAll,
    getCheckboxProps: record => {
      if (record.isHeader) {
        return {
          disabled: true,
          style: { display: 'none' },
        };
      }

      if (!isUserInSubUnitChecker(record)) {
        return {
          disabled: true,
        };
      }
    },
  };

  useEffect(() => {
    dispatch(setFromActivityLogs(false));
  }, [dispatch]);

  // ADVANCEMENT TAB GROUPING
  const [isGroupBy, setIsGroupBy] = useState(false);
  const [isGroupByTemp, setIsGroupByTemp] = useState(false);

  const handleChange = () => e => {
    const { checked } = e.target;
    setIsGroupByTemp(checked);
  };

  const handleSubmit = () => {
    setIsGroupBy(isGroupByTemp);
  };

  const newItems =
    isGroupBy &&
    [Program.BOY_SCOUT, Program.CUB_SCOUT].includes(currentOrg.program)
      ? groupBySubUnitToApprove(itemsPerSubUnit, items)
      : items;

  const renderTitle = () => (
    <React.Fragment>
      {!showForBetaUnits && (
        <RunReportDropdown
          disabled={isOffline || areAnyAdvancementsSelected}
          showAllUsers
        />
      )}
      {canApprove && showUpdate && (
        <ApproveAdvancementsButton
          className={styles.approveAdvancements}
          disabled={isOffline || !areAnyAdvancementsSelected}
          personsAdvancements={selectedAdvancements}
          advancementStatus={isAwarding ? advancementStatuses.AWARDED : null}
        />
      )}
      {canApprove && showUpdate && showDeclineButton && (
        <DeclineAdvancementsButton
          className={styles.approveAdvancements}
          disabled={disableDecline}
          personsAdvancements={selectedAdvancements}
        />
      )}
      {showFilter && !showPOID && (
        <PendingItemsFilterCard>
          <FilterButton />
        </PendingItemsFilterCard>
      )}
      {showPOID && (
        <GroupBy
          onChange={handleChange}
          onSubmit={handleSubmit}
          groupByStatus={isGroupByTemp}
        />
      )}
    </React.Fragment>
  );

  return (
    <Table
      className={styles.table}
      size="small"
      fixedLayout
      dataSource={newItems}
      loading={loading}
      rowKey="key"
      title={renderTitle}
      columns={
        showPOID
          ? advancementsTabColumns({
              sorter,
              onGoToYouthProfile,
              onGoToAdultProfile,
              onGoToPODetails,
            })
          : columns({
              sorter,
              onGoToYouthProfile,
              onGoToAdultProfile,
              onGoToPODetails,
            })
      }
      pagination={pagination}
      sorter={sorter}
      rowSelection={rowSelection}
      onRow={record => ({ onClick: () => handleSelectRow(record) })}
      components={{
        body: {
          row: PendingItemsCustomRow,
        },
      }}
      rowClassName={record =>
        record.isHeader ? styles.rowItemGroup : styles.rowItem
      }
      onChange={handleTableChange}
    />
  );
};

PendingItemsTable.propTypes = {
  items: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  sorter: PropTypes.object.isRequired,
  isOffline: PropTypes.bool.isRequired,
  showFilter: PropTypes.bool,
  selectedRowKeys: PropTypes.array,
  onSetSelectedRowKeys: PropTypes.func,
  onGoToYouthProfile: PropTypes.func,
  onGoToAdultProfile: PropTypes.func,
  onSorterChange: PropTypes.func,
  showPOID: PropTypes.bool,
  showUpdate: PropTypes.bool,
  isAwarding: PropTypes.bool,
};

export default PendingItemsTable;
